// src/components/navigation/NavigationMap.jsx
import React, { useMemo, useEffect, useState } from 'react';
import { MapContainer, ImageOverlay, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, Typography, Alert, useMediaQuery, useTheme } from '@mui/material';
import POIMarker, { getPOITypeKey } from '../maps/POIMarker';
import POILegend from '../maps/POILegend';

/**
 * Get bounds from floor plan (handles both nested object and flat snake_case properties)
 */
const getFloorPlanBounds = (floorPlan) => {
  // Check for nested bounds object
  if (floorPlan?.bounds?.north && floorPlan?.bounds?.south) {
    return [
      [floorPlan.bounds.south, floorPlan.bounds.west],
      [floorPlan.bounds.north, floorPlan.bounds.east],
    ];
  }
  // Check for flat properties (snake_case from API)
  if (floorPlan?.bounds_north && floorPlan?.bounds_south) {
    return [
      [parseFloat(floorPlan.bounds_south), parseFloat(floorPlan.bounds_west)],
      [parseFloat(floorPlan.bounds_north), parseFloat(floorPlan.bounds_east)],
    ];
  }
  return null;
};

/**
 * Convert pixel coordinates to geographic coordinates using floor plan bounds
 */
const pixelToGeo = (x, y, floorPlan) => {
  const bounds = getFloorPlanBounds(floorPlan);
  if (!bounds || !floorPlan?.image_width || !floorPlan?.image_height) return null;

  const [sw, ne] = bounds;
  const xRatio = x / floorPlan.image_width;
  const yRatio = y / floorPlan.image_height;

  // Convert to geo coords (y is inverted - 0 is top in images, but higher lat is north)
  const lat = ne[0] - yRatio * (ne[0] - sw[0]);
  const lng = sw[1] + xRatio * (ne[1] - sw[1]);

  return [lat, lng];
};

/**
 * Custom hook to animate map view to fit route bounds
 */
const FitBoundsComponent = ({ routeCoords, currentStepIndex }) => {
  const map = useMap();

  useEffect(() => {
    if (routeCoords && routeCoords.length > 0) {
      const bounds = L.latLngBounds(routeCoords);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 20 });
    }
  }, [map, routeCoords, currentStepIndex]);

  return null;
};

/**
 * Get image URL from floor plan
 */
const getFloorPlanImageUrl = (floorPlan) => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
  const imageUrl = floorPlan?.image_url || floorPlan?.imageUrl;
  if (!imageUrl) return null;
  return imageUrl.startsWith('http') ? imageUrl : `${API_BASE_URL}${imageUrl}`;
};

/**
 * Navigation map with route display
 * Shows current floor plan, route with animated arrows, current step, and user position
 */
const NavigationMap = ({
  floorPlan,
  route,
  currentStepIndex = 0,
  userPosition = null,
  style = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Calculate route coordinates from steps
  const routeData = useMemo(() => {
    if (!route?.steps || !floorPlan) return { coords: [], segments: [], startCoord: null, endCoord: null };

    const coords = [];
    const segments = [];

    // Build route path from steps
    route.steps.forEach((step, index) => {
      const pathGeo = step.path_geometry;
      if (pathGeo) {
        let startCoord = null;
        let endCoord = null;

        // Convert pixel positions to geo coordinates
        if (pathGeo.start?.x !== undefined && pathGeo.start?.y !== undefined) {
          startCoord = pixelToGeo(pathGeo.start.x, pathGeo.start.y, floorPlan);
        }
        if (pathGeo.end?.x !== undefined && pathGeo.end?.y !== undefined) {
          endCoord = pixelToGeo(pathGeo.end.x, pathGeo.end.y, floorPlan);
        }

        if (startCoord && endCoord) {
          // Add start coord (avoid duplicates)
          if (coords.length === 0 || coords[coords.length - 1][0] !== startCoord[0]) {
            coords.push(startCoord);
          }
          coords.push(endCoord);

          segments.push({
            index,
            start: startCoord,
            end: endCoord,
            isCurrentStep: index === currentStepIndex,
            isCompleted: index < currentStepIndex,
          });
        }
      }
    });

    return {
      coords,
      segments,
      startCoord: coords[0] || null,
      endCoord: coords[coords.length - 1] || null,
    };
  }, [route, floorPlan, currentStepIndex]);

  if (!floorPlan) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Alert severity="info">Loading floor plan...</Alert>
      </Box>
    );
  }

  // Calculate bounds from floor plan geo-reference
  const bounds = getFloorPlanBounds(floorPlan);
  const imageUrl = getFloorPlanImageUrl(floorPlan);

  if (!bounds) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Alert severity="warning">Floor plan is not geo-referenced</Alert>
      </Box>
    );
  }

  const mapCenter = [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2];

  // User position icon (blue dot with pulse)
  const userIcon = new L.DivIcon({
    className: 'user-position-marker',
    html: `<div style="
      width: 20px;
      height: 20px;
      background: #2196F3;
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      animation: pulse 2s infinite;
    "></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });

  // Start marker (green with entrance icon)
  const startIcon = new L.DivIcon({
    className: 'start-marker',
    html: `<div style="
      width: 32px;
      height: 32px;
      background: #4CAF50;
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 3px 8px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
        <path d="M12 3L4 9v12h5v-7h6v7h5V9l-8-6z"/>
      </svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });

  // Destination marker (red pulsing)
  const destinationIcon = new L.DivIcon({
    className: 'destination-marker',
    html: `<div style="
      width: 36px;
      height: 36px;
      background: #F44336;
      border: 4px solid white;
      border-radius: 50%;
      box-shadow: 0 4px 12px rgba(244,67,54,0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      animation: pulse-destination 1.5s infinite;
    ">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
      </svg>
    </div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });

  // Current position marker for current step
  const currentStepIcon = new L.DivIcon({
    className: 'current-step-marker',
    html: `<div style="
      width: 24px;
      height: 24px;
      background: #FF9800;
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 3px 8px rgba(255,152,0,0.5);
      animation: bounce 1s infinite;
    "></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });

  // Get entrance location from route
  const entranceCoords = route?.route?.start_entrance?.geo_lat && route?.route?.start_entrance?.geo_lng
    ? [parseFloat(route.route.start_entrance.geo_lat), parseFloat(route.route.start_entrance.geo_lng)]
    : routeData.startCoord;

  // Use calculated end coord or fallback to entrance
  const destCoords = routeData.endCoord;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        ...style,
      }}
    >
      {/* CSS for animations */}
      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes pulse-destination {
          0% { transform: scale(1); box-shadow: 0 4px 12px rgba(244,67,54,0.5); }
          50% { transform: scale(1.1); box-shadow: 0 4px 20px rgba(244,67,54,0.7); }
          100% { transform: scale(1); box-shadow: 0 4px 12px rgba(244,67,54,0.5); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        @keyframes dash {
          to { stroke-dashoffset: -20; }
        }
        .animated-route {
          stroke-dasharray: 10, 10;
          animation: dash 1s linear infinite;
        }
      `}</style>

      <MapContainer
        center={entranceCoords || mapCenter}
        zoom={20}
        style={{ width: '100%', height: '100%' }}
        zoomControl={!isMobile}
        attributionControl={false}
      >
        {bounds && imageUrl && (
          <ImageOverlay url={imageUrl} bounds={bounds} opacity={1} zIndex={1} />
        )}

        {/* Fit map to route bounds */}
        <FitBoundsComponent routeCoords={routeData.coords} currentStepIndex={currentStepIndex} />

        {/* Route path - completed segments (solid gray) */}
        {routeData.segments
          .filter(seg => seg.isCompleted)
          .map((seg, i) => (
            <Polyline
              key={`completed-${i}`}
              positions={[seg.start, seg.end]}
              pathOptions={{
                color: '#9E9E9E',
                weight: 4,
                opacity: 0.6,
              }}
            />
          ))}

        {/* Route path - remaining segments (blue dashed animated) */}
        {routeData.segments
          .filter(seg => !seg.isCompleted && !seg.isCurrentStep)
          .map((seg, i) => (
            <Polyline
              key={`remaining-${i}`}
              positions={[seg.start, seg.end]}
              pathOptions={{
                color: '#2196F3',
                weight: 4,
                opacity: 0.7,
                dashArray: '10, 10',
                className: 'animated-route',
              }}
            />
          ))}

        {/* Current step segment (bright orange, thicker, animated) */}
        {routeData.segments
          .filter(seg => seg.isCurrentStep)
          .map((seg, i) => (
            <Polyline
              key={`current-${i}`}
              positions={[seg.start, seg.end]}
              pathOptions={{
                color: '#FF9800',
                weight: 6,
                opacity: 1,
                dashArray: '8, 8',
                className: 'animated-route',
              }}
            />
          ))}

        {/* Destination marker */}
        {destCoords && (
          <Marker position={destCoords} icon={destinationIcon} />
        )}

        {/* Start/Entrance marker */}
        {entranceCoords && (
          <Marker position={entranceCoords} icon={startIcon} />
        )}

        {/* Current step indicator */}
        {routeData.segments[currentStepIndex]?.start && (
          <Marker position={routeData.segments[currentStepIndex].start} icon={currentStepIcon} />
        )}

        {/* User position */}
        {userPosition && (
          <Marker position={[userPosition.lat, userPosition.lng]} icon={userIcon} />
        )}

        {/* POI markers from floor plan */}
        {floorPlan?.pointsOfInterest?.map((poi) => (
          <POIMarker key={poi.id} poi={poi} floorPlan={floorPlan} />
        ))}
      </MapContainer>

      {/* Route info overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: isMobile ? 8 : 60,
          left: isMobile ? 8 : 60,
          zIndex: 1000,
          backgroundColor: 'rgba(255,255,255,0.95)',
          padding: 1.5,
          borderRadius: 2,
          boxShadow: 3,
          maxWidth: isMobile ? 'calc(100% - 80px)' : 250,
        }}
      >
        {route?.route && (
          <>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Navigation Route
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Step {currentStepIndex + 1} of {route.steps?.length || 0}
            </Typography>
            {(route.route.total_distance_meters > 0 || route.totalDistance > 0) && (
              <Typography variant="caption" color="text.secondary" display="block">
                Distance: {Math.round(route.route.total_distance_meters || route.totalDistance || 0)}m
              </Typography>
            )}
            {(route.route.total_time_seconds > 0 || route.totalTime > 0) && (
              <Typography variant="caption" color="text.secondary" display="block">
                Est. time: {Math.round((route.route.total_time_seconds || route.totalTime || 0) / 60)} min
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* POI Legend - shows types of POIs visible on the map */}
      {floorPlan?.pointsOfInterest?.length > 0 && (
        <POILegend
          visibleTypes={[
            ...new Set(
              floorPlan.pointsOfInterest.map(poi => getPOITypeKey(poi))
            )
          ]}
          compact={isMobile}
        />
      )}
    </Box>
  );
};

export default NavigationMap;
